import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { AdReviewResult } from '../components/AdReviewResult';
import { api, ApiError } from '../lib/api';
import {
  AD_REVIEW_MEDIA_LABELS,
  type AdReviewMedia,
  type AdReviewMode,
  type AdReviewRequest,
  type AdReviewResponse,
  type AdReviewViewMode,
} from '@shared/adReview';

type ReviewState = 'idle' | 'saving' | 'saved' | 'error';

const MEDIA_ORDER: AdReviewMedia[] = ['gaten', 'indeed', 'engage', 'kyujinbox', 'sukedachi', 'other'];

export default function AdReview() {
  const [draftTitle, setDraftTitle] = useState('');
  const [draftBody, setDraftBody] = useState('');
  const [salaryText, setSalaryText] = useState('');
  const [media, setMedia] = useState<AdReviewMedia[]>(['gaten']);
  const [clientInfo, setClientInfo] = useState('');
  const [targetPersona, setTargetPersona] = useState('');
  const [viewMode, setViewMode] = useState<AdReviewViewMode>('detail');
  const [mode, setMode] = useState<AdReviewMode>('diagnose');

  const [state, setState] = useState<ReviewState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [usedModel, setUsedModel] = useState<string | null>(null);

  const toggleMedia = (m: AdReviewMedia) => {
    setMedia((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));
  };

  const submit = async () => {
    if (!draftBody.trim()) {
      setError('求人原稿の本文を入力してください。');
      return;
    }
    setState('saving');
    setError(null);
    const payload: AdReviewRequest = {
      draftTitle: draftTitle.trim() || undefined,
      draftBody: draftBody.trim(),
      salaryText: salaryText.trim() || undefined,
      media,
      clientInfo: clientInfo.trim() || undefined,
      targetPersona: targetPersona.trim() || undefined,
      viewMode,
      mode,
    };
    try {
      const res = await api.post<AdReviewResponse>('/api/ad-review', payload);
      setResult(res.result);
      setUsedModel(res.model);
      setState('saved');
      setTimeout(() => setState((s) => (s === 'saved' ? 'idle' : s)), 1500);
    } catch (e) {
      setState('error');
      setError(e instanceof ApiError ? e.message : '添削に失敗しました。');
    }
  };

  return (
    <div className="min-h-screen pb-16">
      <Header />
      <main className="mx-auto max-w-6xl px-4 py-6">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">求人原稿の添削(AI)</h1>
            <p className="mt-1 text-sm text-slate-500">
              公開予定の求人原稿のみを貼り付けてください。求職者の個人情報・非公開情報は入力しないでください。
            </p>
          </div>
          <Link to="/" className="btn-secondary">
            ダッシュボードに戻る
          </Link>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* 入力 */}
          <section className="space-y-4">
            <div className="card space-y-4">
              <div>
                <label className="label" htmlFor="ad-title">
                  タイトル(任意)
                </label>
                <input
                  id="ad-title"
                  type="text"
                  className="input"
                  value={draftTitle}
                  onChange={(e) => setDraftTitle(e.target.value)}
                  placeholder="例:【電気工事士】未経験歓迎/資格取得支援あり"
                />
              </div>

              <div>
                <label className="label" htmlFor="ad-body">
                  求人原稿 本文 <span className="text-rank-1">*</span>
                </label>
                <textarea
                  id="ad-body"
                  className="input min-h-[220px] resize-y font-mono text-xs leading-relaxed"
                  value={draftBody}
                  onChange={(e) => setDraftBody(e.target.value)}
                  placeholder="求人原稿の全文テキストを貼り付けてください。"
                />
              </div>

              <div>
                <label className="label" htmlFor="ad-salary">
                  給与・待遇(任意・本文に含む場合は不要)
                </label>
                <textarea
                  id="ad-salary"
                  className="input min-h-[80px] resize-y text-sm"
                  value={salaryText}
                  onChange={(e) => setSalaryText(e.target.value)}
                  placeholder="例:月給25万〜35万円/各種手当/賞与年2回"
                />
              </div>
            </div>

            <div className="card space-y-4">
              <div>
                <span className="label">想定媒体(複数選択可)</span>
                <div className="flex flex-wrap gap-2">
                  {MEDIA_ORDER.map((m) => {
                    const active = media.includes(m);
                    return (
                      <button
                        key={m}
                        type="button"
                        onClick={() => toggleMedia(m)}
                        className={`rounded-md border-2 px-3 py-1.5 text-sm font-medium transition ${
                          active
                            ? 'border-brand bg-brand text-white'
                            : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        {AD_REVIEW_MEDIA_LABELS[m]}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="label">出力モード</span>
                  <div className="flex gap-2">
                    {(['detail', 'summary'] as AdReviewViewMode[]).map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setViewMode(v)}
                        className={`flex-1 rounded-md border-2 px-2 py-1.5 text-xs font-medium transition ${
                          viewMode === v
                            ? 'border-brand-accent bg-brand-accent/10 text-brand-accent'
                            : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        {v === 'detail' ? '詳細(担当者向け)' : 'サマリー(経営者向け)'}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <span className="label">添削モード</span>
                  <div className="flex gap-2">
                    {(['diagnose', 'rewrite'] as AdReviewMode[]).map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setMode(m)}
                        className={`flex-1 rounded-md border-2 px-2 py-1.5 text-xs font-medium transition ${
                          mode === m
                            ? 'border-brand-accent bg-brand-accent/10 text-brand-accent'
                            : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        {m === 'diagnose' ? '診断のみ' : '改善版も生成'}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="label" htmlFor="ad-client">
                  クライアント情報(任意・公開情報のみ)
                </label>
                <input
                  id="ad-client"
                  type="text"
                  className="input"
                  value={clientInfo}
                  onChange={(e) => setClientInfo(e.target.value)}
                  placeholder="例:電気工事業/愛知県/創業30年/資格取得支援が強み"
                />
              </div>

              <div>
                <label className="label" htmlFor="ad-persona">
                  採用ターゲット(任意)
                </label>
                <input
                  id="ad-persona"
                  type="text"
                  className="input"
                  value={targetPersona}
                  onChange={(e) => setTargetPersona(e.target.value)}
                  placeholder="例:20〜30代未経験/手に職をつけたい層"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button onClick={submit} disabled={state === 'saving'} className="btn-primary">
                {state === 'saving' ? '添削中...' : '添削する'}
              </button>
              <ReviewIndicator state={state} />
            </div>

            {error && (
              <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-rank-1">{error}</div>
            )}
          </section>

          {/* 結果(整形プレビュー / 編集トグル) */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                添削結果
              </h2>
              {usedModel && <p className="text-xs text-slate-400">モデル: {usedModel}</p>}
            </div>

            {result === null ? (
              <div className="card grid min-h-[400px] place-items-center text-sm text-slate-400">
                {state === 'saving'
                  ? 'Gemini が添削中です...'
                  : '左のフォームに原稿を貼り付けて「添削する」を押してください。'}
              </div>
            ) : (
              <AdReviewResult markdown={result} onChange={setResult} />
            )}
          </section>
        </div>
      </main>
    </div>
  );
}

function ReviewIndicator({ state }: { state: ReviewState }) {
  const map: Record<ReviewState, { text: string; color: string }> = {
    idle: { text: '', color: 'text-slate-400' },
    saving: { text: '添削中...', color: 'text-slate-500' },
    saved: { text: '✓ 完了しました', color: 'text-rank-5' },
    error: { text: '失敗しました', color: 'text-rank-1' },
  };
  const v = map[state];
  return <span className={`text-xs ${v.color}`}>{v.text}</span>;
}
